import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Mail, Save, RotateCcw, Eye, Send, Check, Plus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RichEditor } from "@/components/RichEditor";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const DEST_LABELS = { staff: "Staff", cliente: "Cliente" };

export default function EmailTemplates() {
  const { hasPerm } = useAuth();
  const canEdit = hasPerm("email_templates.edit");
  const [templates, setTemplates] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [form, setForm] = useState({ oggetto: "", corpo: "", attivo: true });
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [preview, setPreview] = useState({ oggetto: "", html: "" });
  const [testOpen, setTestOpen] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const corpoRef = useRef(null);
  const corpoEditorRef = useRef(null);
  const oggettoRef = useRef(null);
  const lastFocused = useRef("corpo");
  const [editorVersion, setEditorVersion] = useState(0);

  const selected = templates.find((t) => t.key === selectedKey) || null;

  const load = async () => {
    const res = await api.get("/email-templates");
    setTemplates(res.data);
    if (!selectedKey && res.data.length) selectTemplate(res.data[0]);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const selectTemplate = (t) => {
    setSelectedKey(t.key);
    setForm({ oggetto: t.oggetto, corpo: t.corpo, attivo: t.attivo });
    setEditorVersion((n) => n + 1);
  };

  const insertVar = (v) => {
    const token = `{{${v}}}`;
    if (lastFocused.current === "corpo" && corpoEditorRef.current) {
      corpoEditorRef.current.insertToken(token);
      return;
    }
    const target = lastFocused.current === "oggetto" ? "oggetto" : "corpo";
    const ref = target === "oggetto" ? oggettoRef.current : corpoRef.current;
    const cur = form[target] || "";
    if (ref && typeof ref.selectionStart === "number") {
      const s = ref.selectionStart, e = ref.selectionEnd;
      const next = cur.slice(0, s) + token + cur.slice(e);
      setForm((f) => ({ ...f, [target]: next }));
      setTimeout(() => { ref.focus(); ref.setSelectionRange(s + token.length, s + token.length); }, 0);
    } else {
      setForm((f) => ({ ...f, [target]: cur + token }));
    }
  };

  const save = async () => {
    if (!form.oggetto.trim()) { toast.error("Oggetto obbligatorio"); return; }
    setSaving(true);
    try {
      await api.put(`/email-templates/${selectedKey}`, form);
      toast.success("Template salvato");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore salvataggio"); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm("Ripristinare il testo predefinito di questo template?")) return;
    try {
      const res = await api.post(`/email-templates/${selectedKey}/reset`);
      toast.success("Template ripristinato");
      setForm({ oggetto: res.data.oggetto, corpo: res.data.corpo, attivo: res.data.attivo });
      setEditorVersion((n) => n + 1);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const doPreview = async () => {
    try {
      const res = await api.post(`/email-templates/${selectedKey}/preview`, { oggetto: form.oggetto, corpo: form.corpo });
      setPreview(res.data); setPreviewOpen(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore anteprima"); }
  };

  const sendTest = async () => {
    if (!testTo || !testTo.includes("@")) { toast.error("Inserisci un'email valida"); return; }
    setSendingTest(true);
    try {
      const res = await api.post(`/email-templates/${selectedKey}/test`, { to: testTo, oggetto: form.oggetto, corpo: form.corpo });
      toast.success(`Email di test inviata a ${res.data.to}`);
      setTestOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Invio non riuscito"); }
    finally { setSendingTest(false); }
  };

  const grouped = templates.reduce((acc, t) => {
    (acc[t.destinatario] = acc[t.destinatario] || []).push(t); return acc;
  }, {});

  return (
    <div className="p-5 sm:p-8 max-w-7xl mx-auto fade-in" data-testid="email-templates-page">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Comunicazioni · Resend</div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight">TEMPLATE EMAIL</h1>
        <p className="text-sm text-zinc-500 mt-2">Personalizza oggetto e contenuto delle email di sistema usando le variabili dinamiche.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
        {/* Lista template */}
        <div className="space-y-4" data-testid="template-list">
          {Object.keys(grouped).map((dest) => (
            <div key={dest}>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2 px-1">{DEST_LABELS[dest] || dest}</div>
              <div className="space-y-1">
                {grouped[dest].map((t) => (
                  <button key={t.key} onClick={() => selectTemplate(t)}
                    data-testid={`template-item-${t.key}`}
                    className={`w-full text-left px-3 py-2.5 rounded-sm border transition-colors ${
                      selectedKey === t.key
                        ? "bg-[#f98125] text-white border-[#f98125]"
                        : "bg-zinc-900 border-zinc-800 text-zinc-300 hover:border-zinc-700"
                    }`}>
                    <div className="text-sm font-medium flex items-center gap-2">
                      <Mail className="h-3.5 w-3.5 shrink-0" /> {t.nome}
                    </div>
                    <div className={`text-[11px] mt-0.5 flex items-center gap-1.5 ${selectedKey === t.key ? "text-white/80" : "text-zinc-500"}`}>
                      {t.is_custom ? "Personalizzato" : "Predefinito"}
                      {!t.attivo && <span className="text-red-400">· Disattivo</span>}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Editor */}
        {selected && (
          <div className="space-y-5" data-testid="template-editor">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h2 className="text-lg font-semibold">{selected.nome}</h2>
                <p className="text-sm text-zinc-500">{selected.descrizione}</p>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-xs text-zinc-500">Attivo</Label>
                <Switch checked={form.attivo} disabled={!canEdit}
                  onCheckedChange={(v) => setForm((f) => ({ ...f, attivo: v }))}
                  data-testid="template-active-switch" />
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Oggetto</Label>
              <Input ref={oggettoRef} value={form.oggetto} disabled={!canEdit}
                onFocus={() => (lastFocused.current = "oggetto")}
                onChange={(e) => setForm((f) => ({ ...f, oggetto: e.target.value }))}
                className="mt-1.5" data-testid="template-subject-input" />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Corpo</Label>
              {canEdit ? (
                <div className="mt-1.5">
                  <RichEditor key={`${selectedKey}-${editorVersion}`} ref={corpoEditorRef}
                    value={form.corpo}
                    onFocus={() => (lastFocused.current = "corpo")}
                    onChange={(html) => setForm((f) => ({ ...f, corpo: html }))}
                    testid="template-body-editor" />
                  <p className="text-[11px] text-zinc-500 mt-1.5">Editor visuale: usa la barra strumenti per formattare. Le variabili qui sotto si inseriscono nel punto del cursore.</p>
                </div>
              ) : (
                <div className="mt-1.5 rounded-sm border border-zinc-800 bg-[#0A0A0A] px-4 py-3 text-sm text-zinc-100 [&_a]:text-[#f98125] [&_a]:underline" data-testid="template-body-readonly" dangerouslySetInnerHTML={{ __html: form.corpo }} />
              )}
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Variabili disponibili (clic per inserire)</Label>
              <div className="flex flex-wrap gap-2">
                {(selected.variabili || []).map((v) => (
                  <button key={v.var} onClick={() => insertVar(v.var)} disabled={!canEdit}
                    title={v.descrizione} data-testid={`var-chip-${v.var}`}
                    className="flex items-center gap-1 text-xs font-mono bg-zinc-900 border border-zinc-800 text-[#f98125] px-2.5 py-1.5 rounded-sm hover:border-[#f98125] transition-colors disabled:opacity-50">
                    <Plus className="h-3 w-3" />{`{{${v.var}}}`}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap gap-3 pt-2 border-t border-zinc-800">
              <button onClick={doPreview} data-testid="template-preview-btn"
                className="flex items-center gap-2 rounded-sm border border-zinc-700 px-4 py-2.5 text-sm font-medium hover:border-zinc-500 transition-colors">
                <Eye className="h-4 w-4" /> Anteprima
              </button>
              {canEdit && (
                <>
                  <button onClick={() => { setTestTo(""); setTestOpen(true); }} data-testid="template-test-btn"
                    className="flex items-center gap-2 rounded-sm border border-zinc-700 px-4 py-2.5 text-sm font-medium hover:border-zinc-500 transition-colors">
                    <Send className="h-4 w-4" /> Invia test
                  </button>
                  {selected.is_custom && (
                    <button onClick={reset} data-testid="template-reset-btn"
                      className="flex items-center gap-2 rounded-sm border border-zinc-700 px-4 py-2.5 text-sm font-medium text-zinc-400 hover:text-white hover:border-zinc-500 transition-colors">
                      <RotateCcw className="h-4 w-4" /> Ripristina
                    </button>
                  )}
                  <button onClick={save} disabled={saving} data-testid="template-save-btn"
                    className="flex items-center gap-2 rounded-sm bg-[#f98125] text-white font-bold uppercase tracking-wide px-5 py-2.5 hover:bg-[#d96a12] transition-colors active:scale-95 disabled:opacity-60 ml-auto">
                    <Save className="h-4 w-4" /> {saving ? "Salvataggio…" : "Salva"}
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Anteprima */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl" data-testid="preview-dialog">
          <DialogHeader>
            <DialogTitle>Anteprima · {preview.oggetto}</DialogTitle>
            <DialogDescription>Anteprima dell'email con dati di esempio.</DialogDescription>
          </DialogHeader>
          <div className="border border-zinc-800 rounded-sm overflow-hidden bg-white">
            <iframe title="preview" srcDoc={preview.html} className="w-full h-[420px]" data-testid="preview-iframe" />
          </div>
          <p className="text-xs text-zinc-500">Le variabili sono compilate con dati di esempio.</p>
        </DialogContent>
      </Dialog>

      {/* Invio test */}
      <Dialog open={testOpen} onOpenChange={setTestOpen}>
        <DialogContent className="max-w-md" data-testid="test-dialog">
          <DialogHeader>
            <DialogTitle>Invia email di test</DialogTitle>
            <DialogDescription>Invia una copia di prova del template a un indirizzo email.</DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500">Destinatario</Label>
            <Input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)}
              placeholder="tua@email.it" className="mt-1.5" data-testid="test-email-input" />
            <p className="text-xs text-zinc-500 mt-2">Verrà inviata l'email con i dati di esempio e l'oggetto preceduto da [TEST].</p>
          </div>
          <DialogFooter>
            <button onClick={sendTest} disabled={sendingTest} data-testid="test-send-btn"
              className="flex items-center gap-2 rounded-sm bg-[#f98125] text-white font-bold uppercase tracking-wide px-5 py-2.5 hover:bg-[#d96a12] transition-colors active:scale-95 disabled:opacity-60">
              <Check className="h-4 w-4" /> {sendingTest ? "Invio…" : "Invia"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
